import { useCallback, useEffect, useState } from 'react';
import { useAuth, useClerk, UserButton, useUser } from '@clerk/react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import '../styles/workspace.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.horizontl.io';
const NAVIGATION = [
  ['/home', 'Dashboard'],
  ['/review-queue', 'Review Queue'],
  ['/tickets', 'Tickets'],
  ['/doc-feedback', 'Doc Feedback'],
  ['/settings', 'Settings'],
];

const TITLES = {
  '/home': 'Dashboard',
  '/review-queue': 'Review Queue',
  '/tickets': 'Tickets',
  '/doc-feedback': 'Documentation Feedback',
  '/settings': 'Settings',
};

export default function AppLayout() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { user } = useUser();
  const location = useLocation();
  const [state, setState] = useState({ loading: true, error: '', org: null, tickets: [], onboardingAccess: 'checking' });

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setState(current => ({ ...current, loading: true, error: '' }));
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const orgResponse = await fetch(`${API_URL}/api/me`, { headers });
      const org = await orgResponse.json().catch(() => ({}));
      if (!orgResponse.ok) throw new Error(org.detail || 'Could not load your workspace.');
      let onboardingAccess = 'completed';
      if (org.onboarding?.status !== 'completed') {
        const statusResponse = await fetch(`${API_URL}/api/onboarding/status`, { headers });
        const status = await statusResponse.json().catch(() => ({}));
        if (!statusResponse.ok) throw new Error(status.detail || 'Could not load setup status.');
        const ready = status.jira?.status === 'connected' && status.jira?.projects?.length > 0 && status.ticket_handling?.status === 'configured' && ['connected', 'skipped'].includes(status.confluence?.status);
        onboardingAccess = ready ? 'ready_for_testing' : 'setup_required';
      }
      const ticketResponse = await fetch(`${API_URL}/api/tickets`, { headers });
      const tickets = await ticketResponse.json().catch(() => []);
      if (!ticketResponse.ok) throw new Error('Could not load ticket data.');
      setState({ loading: false, error: '', org, tickets, onboardingAccess });
    } catch (caught) {
      setState(current => ({
        ...current,
        loading: false,
        error: caught.message || 'Could not load your workspace.',
      }));
    }
  }, [getToken]);

  useEffect(() => { refresh(); }, [refresh]);
  if (state.loading) return <div className="full-state">Loading your workspace…</div>;
  if (state.error) return <div className="full-state"><div className="error-banner">{state.error}</div><button className="primary-button" onClick={refresh}>Retry</button><button className="secondary-button" onClick={() => signOut({ redirectUrl: `${window.location.origin}/login` })}>Sign out</button></div>;
  if (state.onboardingAccess === 'setup_required') return <Navigate to="/onboarding" replace />;

  return (
    <div className="workspace-shell">
      <aside className="sidebar"><div className="brand"><span>H</span>Horizontl</div><nav>{NAVIGATION.map(([path, label]) => <NavLink key={path} to={path} className={({ isActive }) => isActive ? 'active' : ''}>{label}</NavLink>)}</nav><div className="sidebar-user">{user?.primaryEmailAddress?.emailAddress}</div></aside>
      <main className="workspace-main"><header className="topbar"><h1>{TITLES[location.pathname] || 'Horizontl'}</h1><div><span className="org-chip">{state.org.org_name}</span><UserButton /></div></header><div className="workspace-content">{state.onboardingAccess === 'ready_for_testing' && !location.search.includes('onboarding_test=1') && <div className="notice-banner"><span>Dana is paused until the controlled workspace test is confirmed and activated.</span><NavLink to="/onboarding">Review setup</NavLink></div>}<Outlet context={{ ...state, refresh }} /></div></main>
    </div>
  );
}
